/**
 * Hook: useProgramacionesCache
 *
 * Caché genérico por clave (cacheKey) para el módulo de Programaciones.
 * Sin integración Socket.io — invalidación manual por cambio de tab/filtro.
 *
 * TTL: 20 minutos.
 */

import { useCallback } from 'react';

const CACHE_TTL = 20 * 60 * 1000; // 20 minutos
const CACHE_VERSION = '1.0';

export const useProgramacionesCache = () => {
  /**
   * Obtener datos del caché si aún son válidos.
   * @param {string} cacheKey
   * @returns {Object|null}
   */
  const getCached = useCallback((cacheKey) => {
    try {
      if (!cacheKey) return null;
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== CACHE_VERSION) return null;
      if (Date.now() - parsed.timestamp > CACHE_TTL) return null;
      return parsed.data;
    } catch {
      return null;
    }
  }, []);

  /**
   * Guardar datos en caché.
   * @param {string} cacheKey
   * @param {Object} data
   */
  const setCached = useCallback((cacheKey, data) => {
    try {
      if (!cacheKey) return;
      localStorage.setItem(
        cacheKey,
        JSON.stringify({ data, timestamp: Date.now(), version: CACHE_VERSION })
      );
    } catch (err) {
      if (err.name === 'QuotaExceededError') localStorage.removeItem(cacheKey);
    }
  }, []);

  /**
   * Eliminar caché para una clave específica.
   * @param {string} cacheKey
   */
  const invalidate = useCallback((cacheKey) => {
    if (cacheKey) localStorage.removeItem(cacheKey);
  }, []);

  return { getCached, setCached, invalidate };
};
